/**
 * Echo Hunt -- the round view models (P9, §10 + §16.0 label hygiene).
 *
 * Two shapes, deliberately kept apart:
 *
 *  - `HuntRoundView` is what the PLAY components may receive. It carries the story's bullets in order
 *    and the count of dream-sourced entries ("X", which §10.1 says to tell the player) -- and NOTHING
 *    else. There is no per-slot label field, no `sourceKind`, no `sourceId`, so a component typed
 *    against it cannot leak provenance even by accident (§16.0). The count is the whole point: knowing
 *    HOW MANY are real is part of the game, knowing WHICH is the game.
 *  - `HuntRevealView` is built only after the round is submitted, and is the first thing in the feature
 *    that carries labels. `SubmitRound_Live` is the only production caller.
 *
 * That split is enforced by shape rather than by discipline, which matters because the reveal is where
 * a leaked label becomes a training signal for the wrong answer (§17 "training 'my-typical → fake'").
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The DB-facing wrapper is
 * `Round_Live.ts`, and the scoring lives in `Scoring.ts`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §10.1, §10.4, §13.1, §16.0, §16.2 P9.
 */
import {Answer, RoundSlot, JudgeLoopSummary} from "./Types.js";
import {DEFAULT_CONFIDENCE, HuntScore, ScoreHunt, SelectMarkedSlotIds, SelectRealSlotIds, SelectTellCandidateSlotIds, ToConfidenceList} from "./Scoring.js";

// the playable round (§10.1) -- LABEL-FREE
// ==========

export type HuntBullet = {
	slotId: string;
	text: string;
};

/** What the Play tab may see before submitting (§16.0). Deliberately minimal: adding a label field
 * here is the one change that would break the game's validity, so the type does not have one. */
export type HuntRoundView = {
	roundId: string;
	/** the story, in woven order */
	bullets: HuntBullet[];
	/** how many bullets came from the journal -- the "X" the player is told (§10.1). This is a COUNT,
	 * not a labelling: it is public information in the game's own rules. */
	realCount: number;
	/** the door the weaver offered, if it offered one (§8.3); P11 acts on it */
	doorDescription?: string|null;
};
/** The minimal round shape `ToHuntRoundView` needs; `EchoRound` is structurally assignable. This is an
 * intentionally structural type so the pure module does not import the DB class. */
export type HuntRoundSource = {
	id: string;
	slots?: RoundSlot[]|n;
	/** §11.5: the way out the weaver offered -- INCLUDING the world it leads to. `doorOffer` rather than
	 * `doorPending` because it is an offer on THIS round; the story holds no copy, and a story-level flag
	 * was the thing that used to go stale. */
	doorOffer?: {description: string, targetWorldId?: string|n}|n;
	/** §11.5: the PLAYER's verdict on that offer (authorial, outside the fiction). Null/absent = unanswered.
	 * A string rather than `DoorVerdict` so this stays structural -- the pure module must not import the DB
	 * class, and the only thing it does with the value is compare it. */
	doorVerdict?: string|n;
	/** §11.5: the CHARACTER's in-fiction decision, written by round generation. */
	doorDecision?: string|n;
	/** §11.5 W7: where the door actually led, when the character took it. */
	doorResolvedWorldId?: string|n;
	answers?: Answer[]|n;
	status?: string|n;
	/** set when the round was submitted; absent = still in progress (P9) */
	revealedAt?: number|n;
	/** the judge loop's verdict (P10). Label-derived, but nothing in it says WHICH bullet is which, so it is
	 * safe to surface on the reveal (and the Play tab only uses it to flag an `easy` round). */
	judgeSummary?: JudgeLoopSummary|n;
};

/** True when the round's text exists. A round whose weave failed has slots but no `finalText`/`wovenText`
 * -- it must not be played, because a half-woven story would show text the weaver never wrote (§16.2
 * P8). */
export function IsRoundPlayable(round: HuntRoundSource|n): boolean {
	const slots = round?.slots ?? [];
	if (slots.length == 0) return false;
	return slots.every(slot=>SlotText(slot).length > 0);
}

/** The text the player reads for one slot: the repaired text if any, else the woven text, else the
 * reshaped one. One definition, so the play view and the reveal cannot show different sentences. */
export function SlotText(slot: RoundSlot): string {
	if (slot.finalText.length > 0) return slot.finalText;
	if (slot.wovenText.length > 0) return slot.wovenText;
	return slot.reshapedText;
}

/** Builds the label-free play view (§16.0). Returns null when the round is not playable, so a caller
 * cannot accidentally render a broken round.
 *
 * `realCount` comes from the slots' labels, because SOMEONE has to count them -- but the count is the
 * only label-derived value that crosses this boundary, and it is information the player is entitled to
 * (§10.1). */
export function ToHuntRoundView(round: HuntRoundSource|n): HuntRoundView|null {
	if (round == null || !IsRoundPlayable(round)) return null;
	const slots = round.slots ?? [];

	return {
		roundId: round.id,
		bullets: slots.map(slot=>({slotId: slot.slotId, text: SlotText(slot)})),
		realCount: slots.filter(slot=>slot.sourceKind == "real").length,
		doorDescription: round.doorOffer?.description ?? null,
	};
}

/** Convenience: the guess-list the UI highlights while playing, given the current confidences (§10.1).
 * Exported from here so the Play tab never reimplements "which marks are in my list". */
export function GetHighlightedSlotIds(round: HuntRoundView, confidences: {slotId: string, confidence: number}[]|n): string[] {
	return SelectMarkedSlotIds(confidences, round.realCount, round.bullets.map(a=>a.slotId));
}

// the door (§8.3, P11)
// ==========

/** True when this round offered a door the PLAYER has not yet ruled on (§11.5 W2).
 * The verdict is offered on the REVEAL, not during play: answering it before the reveal would let the
 * player infer something about the round from the offer, and it is an authorial decision rather than a
 * hunt one. The CHARACTER's decision is separate and comes later (`doorDecision`), which is why this
 * predicate keys on `doorVerdict` and not on the door's continued existence.
 *
 * Takes the round rather than the label-free view for the same reason `IsRoundFlaggedEasy` does: the play
 * view's key set is pinned by a test (§16.0), and "a door is pending" is not a per-bullet label. */
export function HasUnansweredDoor(round: HuntRoundSource|n): boolean {
	if (round?.doorOffer == null) return false;
	return round.doorVerdict == null;
}

/** True when a round was woven in a world (or from a summary) the story has since moved on from (§11.5 W7).
 *
 * A manual world move, or a correction on the Story tab, can leave an unrevealed round behind. Playing it is
 * still fine -- the prose is coherent within its own world, and `ResolveRoundWorld` already judges it against
 * the world it was written in -- but the player must be TOLD, because otherwise the Play tab's own line
 * ("Continuing: X in Y") simply contradicts the round in front of them.
 *
 * This is also exactly the invalidation test P13's one-round lookahead needs: a pre-generated round whose
 * world or summary no longer matches must be discarded rather than shown. */
export function IsRoundStaleAgainstStory(round: HuntRoundSource|n, story: {summary?: string|n}|n, currentWorldId: string|null): boolean {
	if (round == null || round.revealedAt != null) return false; // a played round is history, not stale

	const roundWorldId = (round as {worldId?: string|n}).worldId ?? null;
	if (roundWorldId != null && currentWorldId != null && roundWorldId != currentWorldId) return true;

	// The summary check catches a change the world check cannot see (same world, different stretch). It
	// compares against `storylineSummaryOut`, NOT `storylineSummaryIn`: the story advances to the woven
	// round's OUT summary immediately after the weave (§8.1), so comparing the IN summary would flag every
	// freshly built round as stale.
	const summaryOut = (round as {storylineSummaryOut?: string|n}).storylineSummaryOut ?? null;
	if (summaryOut != null && story?.summary != null && summaryOut != story.summary) return true;

	return false;
}

// judge status (P10, §9.4)
// ==========

/** True when the judge loop ran and FAILED to make the round stranger-proof (§9.4). §9.4 allows either
 * discarding such a round or showing it flagged "easy"; this build always shows it flagged, because a
 * discarded round is one the user paid for and cannot see.
 *
 * Deliberately a function on the ROUND rather than a field on `HuntRoundView`: the play view's exact key set
 * is pinned by a test (§16.0), and "this round was too easy" is not a per-bullet label, so it belongs in a
 * separate helper the Play tab calls with the round it already holds. */
export function IsRoundFlaggedEasy(round: HuntRoundSource|n): boolean {
	if (round?.judgeSummary == null) return false;
	return round.judgeSummary.landedInBand != true;
}

/** True when the round has never been through the judge loop, so the Play tab can offer to judge it (P10).
 * A round generated before P10 has `judgeSummary == null` and `status == "unjudged"`. */
export function IsRoundUnjudged(round: HuntRoundSource|n): boolean {
	if (round == null) return false;
	if (round.judgeSummary != null) return false;
	return round.status == null || round.status == "unjudged";
}

// the reveal (§10.1, §10.4) -- LABELLED
// ==========

/** One real bullet's provenance, shown at the reveal (§10.4 "light touch": the raw bullet and its
 * date; the full dream only on request). */
export type RevealedSource = {
	slotId: string;
	/** the pre-transformation text the corpus was built from (§4.2) */
	rawText: string;
	/** the journal date of the parent dream, if known */
	date?: number|n;
	heading?: string|n;
	/** `entryId/segmentId` -- used to find the parent dream when the user opens it (§10.4) */
	dreamId?: string|n;
};

/** What the reveal shows. Built only after submission. */
export type HuntRevealView = {
	roundId: string;
	score: HuntScore;
	/** the story with its labels, in the same order the player read it */
	slots: RoundSlot[];
	/** provenance for the REAL slots only (fakes have no source, by construction) */
	sources: RevealedSource[];
	/** fakes the user ended up calling fake, i.e. left out of their guessed dream set: the reveal offers
	 * the tell box for these (§10.3). Derived from the scored OUTCOME, not a confidence cutoff -- the
	 * slider is a relative ranking, so the exact number is not a meaningful threshold. */
	tellCandidateSlotIds: string[];
	/** tell phrases already stored on this round's answers (`slotId` -> phrase), so re-opening a revealed
	 * round shows what was typed instead of an empty box (§10.3). An answer with a phrase but no
	 * `llmTellFlag` is still included: the phrase is the fact that matters. */
	tellPhrases: Record<string, string>;
	doorDescription?: string|null;
	/** the judge loop's verdict (P10): how well a stranger could have separated the two classes. Null for a
	 * round that never went through the loop. */
	judgeSummary?: JudgeLoopSummary|null;
};

/** Provenance rows for a round's real slots, keyed by the corpus data the caller supplies. Pure: the
 * caller (`Round_Live`) does the DB lookup and passes plain values in.
 *
 * A real slot whose corpus row has gone missing still gets a row -- with whatever text is available --
 * because silently omitting it would look like the bullet was a fake, which is exactly the confusion
 * the reveal exists to remove. */
export function BuildRevealedSources(slots: RoundSlot[]|n, corpusByBulletId: Map<string, {rawText?: string|n, date?: number|n, heading?: string|n, dreamId?: string|n}>|n): RevealedSource[] {
	const lookup = corpusByBulletId ?? new Map();
	return (slots ?? [])
		.filter(slot=>slot.sourceKind == "real")
		.map(slot=>{
			const row = lookup.get(slot.sourceId);
			return {
				slotId: slot.slotId,
				rawText: row?.rawText ?? slot.reshapedText,
				date: row?.date ?? null,
				heading: row?.heading ?? null,
				dreamId: row?.dreamId ?? null,
			};
		});
}

/** Builds the reveal view (§10.1) from the submitted answers. `sources` is passed in because resolving
 * raw journal text is a DB read and this module stays pure. */
export function BuildRevealView(round: HuntRoundSource|n, answers: Answer[]|n, sources: RevealedSource[]|n, options?: {markedSlotIds?: string[]|n}): HuntRevealView|null {
	if (round == null) return null;
	const slots = round.slots ?? [];
	if (slots.length == 0) return null;

	const score = ScoreHunt(ToConfidenceList(answers), slots, {markedSlotIds: options?.markedSlotIds});
	const sourcesBySlot = new Map((sources ?? []).map(source=>[source.slotId, source]));

	return {
		roundId: round.id,
		score,
		slots,
		// one row per real slot, in story order, falling back to whatever the caller supplied
		sources: slots
			.filter(slot=>slot.sourceKind == "real")
			.map(slot=>sourcesBySlot.get(slot.slotId) ?? {slotId: slot.slotId, rawText: slot.reshapedText}),
		tellCandidateSlotIds: SelectTellCandidateSlotIds(score, slots),
		tellPhrases: CollectStoredTellPhrases(answers),
		doorDescription: round.doorOffer?.description ?? null,
		judgeSummary: round.judgeSummary ?? null,
	};
}

/** The tell phrases already stored on a round's answers, keyed by slot (§10.3). This is what lets the
 * reveal re-open with the user's earlier notes in place rather than blank boxes -- the phrases are on the
 * round already, so the only thing that was ever missing was reading them back. */
export function CollectStoredTellPhrases(answers: Answer[]|n): Record<string, string> {
	const result: Record<string, string> = {};
	for (const answer of answers ?? []) {
		const phrase = answer.tellPhrase?.trim();
		if (phrase != null && phrase.length > 0) result[answer.slotId] = phrase;
	}
	return result;
}

/** Sets, or DELETES (with an empty phrase), one slot's tell phrase (§10.3), returning a new answer list.
 *
 * Clearing also clears `llmTellFlag`: the flag records that the user reported a tell, so leaving it set
 * after the report is deleted would make the round log claim a report that no longer exists.
 *
 * A slot with no answer row yet gets one appended -- `ScoreHunt` treats a missing answer as neutral
 * confidence, which can make an unrated slot a tell candidate, and silently matching nothing would make
 * "Save" a no-op the user could not distinguish from a failure. */
export function SetTellPhraseInAnswers(answers: Answer[]|n, slotId: string, phrase: string|n): Answer[] {
	const normalized = phrase?.trim() ?? "";
	const hasPhrase = normalized.length > 0;
	const list = answers ?? [];

	const updated = list.map(answer=>answer.slotId == slotId
		? {...answer, llmTellFlag: hasPhrase ? true : null, tellPhrase: hasPhrase ? normalized : null}
		: answer);
	if (list.some(answer=>answer.slotId == slotId) || !hasPhrase) return updated;

	return [...updated, {
		slotId,
		marked: false,
		confidence: DEFAULT_CONFIDENCE,
		msToMark: 0,
		llmTellFlag: true,
		tellPhrase: normalized,
	}];
}

/** The slots whose raw journal text the reveal EXPOSES (§10.4). Used by `SubmitRound_Live` to decide
 * which corpus rows get `exposureRevealCount`; opening the full dream is a separate, stronger exposure
 * and is counted at that action instead.
 *
 * Takes only the `sources` slice of a reveal, so a caller (and a test) does not have to build a whole
 * `HuntRevealView` to ask the question. */
export function SelectRevealedSlotIds(reveal: {sources: {slotId: string}[]}|n): string[] {
	return (reveal?.sources ?? []).map(source=>source.slotId);
}

/** The real slots of a round (§13.1: `exposureRoundCount` is incremented for every real bullet SHOWN,
 * whether or not the user got it right). */
export function SelectShownRealSlotIds(round: HuntRoundSource|n): string[] {
	return SelectRealSlotIds(round?.slots ?? []);
}

// progress / restore (§16.2 P9 "reloading mid-round restores progress")
// ==========

/** A round is still being played when it has NOT been revealed. `answers` being empty is not the test:
 * a freshly generated round has none either, and both states need the Play tab to show it. */
export function IsRoundRevealed(round: HuntRoundSource|n): boolean {
	return round?.revealedAt != null;
}

/** True when the round was started but never submitted -- the case a reload must restore (§16.2 P9). */
export function IsRoundInProgress(round: HuntRoundSource|n): boolean {
	if (round == null || IsRoundRevealed(round)) return false;
	return (round.answers ?? []).length > 0;
}

/** Which round the Play tab should show: the newest playable round that has not been revealed yet --
 * whether it is freshly generated or half-played. Sorting by `createdAt` descending matches how the DB
 * orders rounds. */
export function PickPlayableRound<T extends HuntRoundSource & {createdAt: number}>(rounds: T[]|n): T|null {
	const playable = (rounds ?? [])
		.filter(round=>!IsRoundRevealed(round) && IsRoundPlayable(round))
		.sort((a, b)=>b.createdAt - a.createdAt);
	return playable[0] ?? null;
}

/** The rounds that HAVE been revealed, newest first -- what a stats/history view reads (P13). */
export function PickRevealedRounds<T extends HuntRoundSource & {createdAt: number}>(rounds: T[]|n): T[] {
	return (rounds ?? [])
		.filter(round=>IsRoundRevealed(round))
		.sort((a, b)=>b.createdAt - a.createdAt);
}
